import { Heart, FileText, Shield, Users, CheckCircle, AlertTriangle } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const EstatePlanning = () => {
  const mainContent = (
    <>
      {/* Overview Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6">Comprehensive Estate Planning</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Estate planning isn't just for the wealthy—it's for anyone who wants to protect their family and ensure their wishes are honored. Our experienced attorneys help you create a comprehensive plan tailored to your unique situation.
            </p>

            <div className="bg-accent/10 border-l-4 border-accent p-6 rounded-r-lg mb-12">
              <p className="text-lg font-semibold mb-2">
                <AlertTriangle className="inline h-5 w-5 mr-2 text-accent" />
                Did You Know?
              </p>
              <p className="text-muted-foreground">
                Without an estate plan, Texas intestacy laws determine who inherits your assets—and it may not align with your wishes. Don't leave your family's future to chance.
              </p>
            </div>

            <h3 className="text-3xl font-bold mb-8">Essential Estate Planning Documents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <FileText className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Last Will & Testament</h3>
                <p className="text-muted-foreground mb-4">
                  Your will is the foundation of your estate plan, specifying how your assets should be distributed.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Designate beneficiaries for your assets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Name guardians for minor children</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Appoint an executor to manage your estate</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Specify funeral and burial wishes</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Shield className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Living Trust</h3>
                <p className="text-muted-foreground mb-4">
                  Trusts offer greater control, privacy, and can help your family avoid probate entirely.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Avoid probate court process</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Maintain privacy of asset distribution</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Provide for incapacity management</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Control distribution timing</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Users className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Power of Attorney</h3>
                <p className="text-muted-foreground mb-4">
                  Designate someone to make financial and legal decisions if you become incapacitated.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Durable Power of Attorney for finances</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Limited or general authority options</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Immediate or springing effectiveness</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Avoid court-appointed guardianship</span>
                  </li>
                </ul>
              </div>

              <div className="bg-card p-6 rounded-lg border border-accent/20">
                <Heart className="h-8 w-8 text-accent mb-4" />
                <h3 className="text-xl font-bold mb-3">Healthcare Directives</h3>
                <p className="text-muted-foreground mb-4">
                  Ensure your medical wishes are known and respected during critical moments.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Medical Power of Attorney</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>Living Will / Advance Directive</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>HIPAA Authorization</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-1" />
                    <span>DNR orders if desired</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Planning Process */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Estate Planning Process</h2>
            <div className="space-y-6">
              {[
                {
                  step: "1",
                  title: "Initial Consultation",
                  desc: "Discuss your goals, family situation, assets, and concerns in a confidential meeting."
                },
                {
                  step: "2",
                  title: "Asset Review",
                  desc: "Comprehensive review of your property, accounts, business interests, and beneficiary designations."
                },
                {
                  step: "3",
                  title: "Strategy Development",
                  desc: "Create a customized estate plan that minimizes taxes, avoids probate, and protects your family."
                },
                {
                  step: "4",
                  title: "Document Preparation",
                  desc: "Draft all necessary legal documents with precise language tailored to Texas law."
                },
                {
                  step: "5",
                  title: "Review & Signing",
                  desc: "Carefully review all documents, answer questions, and execute with proper witnesses and notarization."
                },
                {
                  step: "6",
                  title: "Implementation & Updates",
                  desc: "Guide you through implementing your plan and offer periodic reviews as your life circumstances change."
                }
              ].map((item) => (
                <div key={item.step} className="flex gap-4 items-start bg-card p-6 rounded-lg">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-gold-shine text-primary font-bold flex items-center justify-center">
                    {item.step}
                  </div>
                  <div>
                    <h4 className="text-lg font-bold mb-1">{item.title}</h4>
                    <p className="text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-center">Benefits of Proper Estate Planning</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Shield className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Asset Protection</h3>
                <p className="text-muted-foreground">Shield your assets from creditors, lawsuits, and unnecessary taxes</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <Users className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Family Protection</h3>
                <p className="text-muted-foreground">Ensure your loved ones are cared for according to your wishes</p>
              </div>
              <div className="text-center p-6">
                <div className="flex justify-center mb-4">
                  <FileText className="h-12 w-12 text-accent" />
                </div>
                <h3 className="text-xl font-bold mb-2">Peace of Mind</h3>
                <p className="text-muted-foreground">Know that your affairs are in order and your legacy is secure</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );

  return (
    <GenericPracticePage
      title="Estate Planning"
      subtitle="Protect Your Family's Future with Comprehensive Estate Planning"
      icon={<Heart className="h-16 w-16" />}
      mainContent={mainContent}
    />
  );
};

export default EstatePlanning;